import { styled } from "goober";
import { useMemo } from "react";
import {
  isRouteType,
  isWebpageRoute,
  pickPrimaryRoute,
  type RouteType,
} from "../../shared/stepConfig";
import { useTenantCustomDomain } from "../hooks/useTenantCustomDomain";
import { useSessionTenantEdgeUrlOptions } from "../hooks/useSessionTenantOrigin";
import type { ParsedStep } from "../lib/parseCommitData";
import { ROUTE_TYPE_ICONS, routeTypeLabel } from "../lib/routeTypeDisplay";
import { getPageUrl } from "../lib/stepUrl";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { META_SEP } from "./MetaSeparator";
import { EmptyState } from "./EmptyState";
import { LiveBrowser } from "./LiveBrowser";
import { useSettledStepContentKey } from "../lib/stepContentKey";
import type { ElementSnippet } from "./Chat/chatTypes";
import type { BranchData, CommitFileData } from "./Workflow";
import { BrowserChrome } from "./BrowserChrome";

type Props = {
  branch: BranchData;
  headCommitFiles: readonly CommitFileData[];
  spaceSlug: string;
  customHostname?: string | null;
  spaceId: string;
  steps: ReadonlyArray<ParsedStep>;
  inspectMode: boolean;
  onInspectModeChange: (mode: boolean) => void;
  onElementSelect: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect: (index: number) => void;
  elementSnippets: readonly ElementSnippet[];
};

const Outer = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

function getRouteType(routeType: string | null): RouteType {
  return isRouteType(routeType) ? routeType : "other";
}

export function WorkflowBrowserPanel({
  branch,
  headCommitFiles,
  spaceSlug,
  customHostname,
  spaceId,
  steps,
  inspectMode,
  onInspectModeChange,
  onElementSelect,
  onElementDeselect,
  elementSnippets,
}: Props) {
  const { browserStep } = useCurrentUrl(routes.space.workflow);
  const customDomain = useTenantCustomDomain();
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const browserRoutes = useMemo(
    () =>
      (branch.headCommit?.routes ?? [])
        .filter((route) => isRouteType(route.routeType))
        .sort((a, b) => a.route.localeCompare(b.route)),
    [branch.headCommit?.routes]
  );
  const stepsByCanonicalId = useMemo(
    () => new Map(steps.map((step) => [step.canonicalId, step])),
    [steps]
  );
  const primaryRoute = useMemo(
    () => pickPrimaryRoute(browserRoutes, browserStep),
    [browserRoutes, browserStep]
  );
  const primaryStep = useMemo(
    () =>
      primaryRoute
        ? (stepsByCanonicalId.get(primaryRoute.stepDir) ?? null)
        : null,
    [primaryRoute, stepsByCanonicalId]
  );
  const fullUrl = primaryRoute
    ? getPageUrl(
        spaceSlug,
        primaryRoute.route,
        edgeUrlOptions,
        { branchId: branch.name === "main" ? null : branch.id },
        customDomain,
        customHostname
      )
    : "";
  const displayUrl = fullUrl.replace(/^https?:\/\//, "");

  const browserLinks = useMemo(
    () =>
      browserRoutes.map((route) => {
        const routeType = getRouteType(route.routeType);
        const step = stepsByCanonicalId.get(route.stepDir);
        return {
          key: `${route.commitId}:${route.route}`,
          icon: ROUTE_TYPE_ICONS[routeType],
          label: route.route,
          metadata: `${routeTypeLabel(routeType)}${META_SEP}${step?.name ?? route.stepDir}`,
          selected:
            route.commitId === primaryRoute?.commitId &&
            route.route === primaryRoute.route,
          onClick: () =>
            navigate(routes.space.workflow, {
              step: "",
              browserStep: route.stepDir,
              tab: "browser",
            }),
        };
      }),
    [browserRoutes, primaryRoute, stepsByCanonicalId]
  );

  const contentKey = useSettledStepContentKey(
    primaryStep?.canonicalId ?? "",
    headCommitFiles,
    branch.headCommitChat?.respondingAt != null
  );

  if (!primaryRoute || !primaryStep) {
    return (
      <Outer>
        <BrowserChrome
          displayUrl={displayUrl}
          openUrl={fullUrl || undefined}
          links={browserLinks}
        >
          <EmptyState
            iconName="earth"
            title="No webpage yet"
            description="If this workflow has a webpage, you’ll be able to interact with it and inspect it here."
          />
        </BrowserChrome>
      </Outer>
    );
  }

  if (
    !isWebpageRoute(primaryRoute.routeType) ||
    !isWebpageRoute(primaryStep.routeType)
  ) {
    return (
      <Outer>
        <BrowserChrome
          displayUrl={displayUrl}
          openUrl={fullUrl || undefined}
          links={browserLinks}
        >
          <EmptyState
            iconName="earth"
            title="Not a webpage"
            description="This workflow’s entry link isn’t a webpage. Check the Links tab to see what’s available."
          />
        </BrowserChrome>
      </Outer>
    );
  }

  return (
    <Outer>
      <LiveBrowser
        step={primaryStep}
        spaceSlug={spaceSlug}
        spaceId={spaceId}
        branchId={branch.id}
        contentKey={contentKey}
        navBranchId={branch.name === "main" ? undefined : branch.id}
        displayUrl={displayUrl}
        openUrl={fullUrl || undefined}
        browserLinks={browserLinks}
        inspectMode={inspectMode}
        onInspectModeChange={onInspectModeChange}
        onElementSelect={onElementSelect}
        onElementDeselect={onElementDeselect}
        elementSnippets={elementSnippets.filter(
          (s) => s.stepName === primaryStep.name
        )}
      />
    </Outer>
  );
}
